import { ReactElement, useState } from 'react'

import { PlusOutlined } from '@ant-design/icons'
import { Button, message } from 'antd'
import { Field, FieldProps, Formik } from 'formik'
import { useTranslation } from 'react-i18next'

import { Flex, Icon, Modal } from 'components/core'
import { CreateOrganizationModal } from 'components/CreateResourceModal'
import Box from 'components/Layout/Box'
import { PermissionTooltip } from 'components/PermissionTooltip/PermissionTooltip'
import OrganizationSearchSelect from 'components/SearchSelect/OrganizationSearchSelect/OrganizationSearchSelect'
import { acceptInvitation } from 'features/Auth/authService'
import {
    userHasCustomerServicePermissions,
    userHasDashboardsOnlyPermissions,
    userHasOrgAdminPermissions,
} from 'helpers/featurePermissions'
import { useAuthDomainValue, useModal, useUserHasPermissions } from 'hooks'
import { Organization, OrganizationInvitation } from 'types'
import { JoinOrganizationForm } from 'views/Auth/Forms'

import * as authStyles from '../../../Auth/styles.scss'

import { ImpersonationButtonToggle } from './ImpersonationButtonToggle'
import * as orgModalStyles from './OrganizationModal.module.scss'

interface FormValues {
    organizationId: string
}

interface Props {
    modalVisible: boolean
    closeModal: () => void
    currentOrganization: Organization
    handleChangeOrganization: (organizationId: string) => void
}

const OrganizationModal = ({
    modalVisible,
    closeModal,
    currentOrganization,
    handleChangeOrganization,
}: Props): ReactElement => {
    const { t } = useTranslation(['common', 'navigation'])

    const pendingInvitations =
        useAuthDomainValue<OrganizationInvitation[]>('pendingInvitations')

    const [activeInvitation, setActiveInvitation] =
        useState<OrganizationInvitation | null>(null)

    const {
        isModalVisible: isAcceptInviteModalVisible,
        toggleModalVisible: toggleAcceptInviteModal,
    } = useModal()

    const {
        isModalVisible: isCreateOrgModalVisible,
        toggleModalVisible: toggleCreateOrgModal,
    } = useModal()

    const isImpersonationMode = useUserHasPermissions(
        userHasCustomerServicePermissions
    )

    const dashboardOnlyExperience = useUserHasPermissions(
        userHasDashboardsOnlyPermissions
    )

    const hasOrgAdminPermissions = useUserHasPermissions(
        userHasOrgAdminPermissions
    )

    const pendingOrgs = [...pendingInvitations].sort((a, b) =>
        a.organization.name.localeCompare(b.organization.name)
    )

    return (
        <>
            <Formik<FormValues>
                initialValues={{
                    organizationId: currentOrganization.id,
                }}
                onSubmit={({ organizationId }) => {
                    closeModal()
                    handleChangeOrganization(organizationId)
                }}
            >
                {({ handleSubmit, setFieldValue }) => (
                    <Modal
                        title={t(
                            'navigation:SideMenu.OrganizationModal.title',
                            'Select Organization'
                        )}
                        open={modalVisible}
                        onCancel={closeModal}
                        footer={
                            <Flex
                                justifyContent="space-between"
                                alignItems="normal"
                            >
                                <Box
                                    flex
                                    alignItems="start"
                                    style={{ flex: 1 }}
                                >
                                    {!dashboardOnlyExperience &&
                                        currentOrganization.enabled &&
                                        !currentOrganization.locked &&
                                        currentOrganization.is_agency === 1 && (
                                            <PermissionTooltip
                                                hasPermission={
                                                    hasOrgAdminPermissions
                                                }
                                            >
                                                <Button
                                                    key="create"
                                                    disabled={
                                                        !hasOrgAdminPermissions
                                                    }
                                                    onClick={() =>
                                                        toggleCreateOrgModal()
                                                    }
                                                    icon={<PlusOutlined />}
                                                >
                                                    {t(
                                                        'navigation:SideMenu.OrganizationModal.createNewOrgBtnText',
                                                        'Create New Organization'
                                                    )}
                                                </Button>
                                            </PermissionTooltip>
                                        )}
                                </Box>
                                <Flex gap={8} alignItems="normal">
                                    <Button key="cancel" onClick={closeModal}>
                                        {t('common:cancel', 'Cancel')}
                                    </Button>
                                    <Button
                                        key="ok"
                                        type="primary"
                                        onClick={() => handleSubmit()}
                                    >
                                        {t('common:ok', 'Ok')}
                                    </Button>
                                </Flex>
                            </Flex>
                        }
                    >
                        <Field name="organizationId">
                            {({
                                field,
                                form: {
                                    values: { organizationId },
                                },
                            }: FieldProps<FormValues>) => (
                                <OrganizationSearchSelect
                                    autoFocus
                                    getPopupContainer={(trigger) =>
                                        trigger.parentElement ?? document.body
                                    }
                                    impersonateAccess={false}
                                    onChange={(value) =>
                                        setFieldValue(field.name, value)
                                    }
                                    selectedOrgId={organizationId}
                                    style={{ width: '100%' }}
                                />
                            )}
                        </Field>
                        {pendingOrgs.length > 0 && (
                            <fieldset
                                className={
                                    orgModalStyles.pendingInvitesFieldset
                                }
                            >
                                <legend className="fg-form-legend">
                                    {t(
                                        'navigation:SideMenu.OrganizationModal.pendingInvites',
                                        'Pending Invites:'
                                    )}
                                </legend>
                                {pendingOrgs.map((invite) => (
                                    <div
                                        key={invite.id}
                                        className={
                                            orgModalStyles.pendingInvitesItem
                                        }
                                    >
                                        <Icon name="NAV_ORG" size={18} />{' '}
                                        <span
                                            className={
                                                orgModalStyles.pendingInvitesItemLabel
                                            }
                                        >
                                            {invite.organization.name}
                                        </span>
                                        <Button
                                            type="link"
                                            onClick={() => {
                                                setActiveInvitation(invite)
                                                toggleAcceptInviteModal()
                                            }}
                                            style={{ paddingLeft: 4 }}
                                        >
                                            (
                                            {t(
                                                'navigation:SideMenu.OrganizationModal.acceptInviteBtnText',
                                                'Accept Pending Invitation'
                                            )}
                                            )
                                        </Button>
                                    </div>
                                ))}
                            </fieldset>
                        )}
                        {isImpersonationMode && (
                            <div style={{ marginTop: 12 }}>
                                <ImpersonationButtonToggle />
                            </div>
                        )}
                    </Modal>
                )}
            </Formik>
            <Modal
                open={isAcceptInviteModalVisible}
                onCancel={toggleAcceptInviteModal}
                footer={null}
            >
                {activeInvitation && (
                    <div className={authStyles.form}>
                        <JoinOrganizationForm
                            invitation={activeInvitation}
                            onSubmit={(values) =>
                                acceptInvitation({
                                    ...values,
                                    callback: () => {
                                        toggleAcceptInviteModal()
                                        message.success(
                                            t(
                                                'navigation:SideMenu.OrganizationModal.acceptInviteSuccessMsg',
                                                'Successfully accepted invitation.'
                                            )
                                        )
                                    },
                                })
                            }
                        />
                    </div>
                )}
            </Modal>
            <CreateOrganizationModal
                visible={isCreateOrgModalVisible}
                closeModal={toggleCreateOrgModal}
            />
        </>
    )
}

export default OrganizationModal
